/**
 * Utilidades para construir URLs del microservicio Colmena (prefijo `/api` + versión `v1`)
 * coherentes con la configuración de `environment`.
 */
import { resolveApiService } from './api-service-routes';
import { environment } from '../../environments/environment';
import type { ColmenaApiService } from '../../environments/environment.model';

/**
 * Elimina barras finales duplicadas en bases de URL o prefijos.
 *
 * @param s Cadena con posibles `/` al final.
 * @returns Misma cadena sin `/` finales.
 */
function trimEndSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

function isAbsoluteUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/** Probes Nest en la raíz del MS (no van bajo `apiPathPrefix`). */
const UNPREFIXED_PROBE_PATHS = ['/health', '/ready'] as const;

function isUnprefixedProbePath(path: string): boolean {
  return UNPREFIXED_PROBE_PATHS.some((probe) => path === probe || path.startsWith(`${probe}/`));
}

/**
 * APIM DESA expone cada MS bajo un slug en el path
 * (`/ingresos-cotizaciones-reglas/api/v1/health`), no en la raíz del host.
 * Detecta orígenes con pathname distinto de `/`.
 */
function serviceOriginHasApimPathPrefix(origin: string): boolean {
  try {
    return new URL(origin).pathname.replace(/\/+$/, '').length > 0;
  } catch {
    return false;
  }
}

/** Probes Nest directos (`/health`) vs APIM (`/{slug}/api/v1/health`). */
function resolveProbeRequestPath(probePath: string, origin: string): string {
  if (!isUnprefixedProbePath(probePath) || !serviceOriginHasApimPathPrefix(origin)) {
    return probePath;
  }
  const prefix = trimEndSlash(environment.apiPathPrefix || '/api/v1');
  return `${prefix}${probePath}`;
}

/**
 * Origen por defecto (gateway único) según el ambiente.
 */
export function resolveApiOrigin(): string {
  const configured = environment.apiBaseUrl?.trim() ?? '';
  if (configured) {
    return trimEndSlash(configured);
  }

  if (environment.useApiProxy) {
    return '';
  }

  if (typeof window === 'undefined' || !window.location?.hostname) {
    return '';
  }

  const protocol = window.location.protocol;
  const host = environment.apiHost?.trim() || window.location.hostname;
  return `${protocol}//${host}:${environment.apiPort}`;
}

/**
 * Origen del microservicio cuando `apiRouting === 'per-service'`.
 * Si no hay origen configurado para el servicio, usa fallback gateway.
 */
export function resolveServiceOrigin(service: ColmenaApiService): string {
  const perService = environment.apiServiceOrigins?.[service]?.trim() ?? '';
  if (perService) {
    return trimEndSlash(perService);
  }
  return resolveApiOrigin();
}

/** Texto legible de a dónde apunta el frontend (mensajes de error / diagnóstico). */
export function describeApiConnection(): string {
  if (environment.apiRouting === 'per-service') {
    const origins = environment.apiServiceOrigins ?? {};
    const entries = Object.entries(origins).filter(([, url]) => url?.trim());
    if (entries.length > 0) {
      return entries.map(([svc, url]) => `${svc}=${url}`).join('; ');
    }
  }

  const configured = environment.apiBaseUrl?.trim() ?? '';
  if (configured) {
    if (isAbsoluteUrl(configured)) {
      return configured;
    }
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${configured.startsWith('/') ? configured : `/${configured}`}`;
    }
    return configured;
  }

  if (environment.useApiProxy) {
    return `mismo origen (proxy ng serve → :${environment.apiPort})`;
  }

  const host =
    environment.apiHost?.trim() ||
    (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
  return `http://${host}:${environment.apiPort}`;
}

/**
 * Construye la URL del API Colmena (prefijo `/api` + versión `v1`).
 * En modo `per-service`, enruta al APIM del microservicio según el path.
 * Los probes `/health` y `/ready` no reciben prefijo: viven en la raíz de cada MS.
 *
 * @param path Ruta relativa del endpoint (con o sin `/` inicial).
 * @returns URL absoluta o relativa lista para `HttpClient`.
 */
export function buildColmenaApiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const prefix = trimEndSlash(environment.apiPathPrefix || '/api/v1');
  const fullPath = isUnprefixedProbePath(normalized)
    ? normalized
    : `${prefix}${normalized}`;

  let origin = resolveApiOrigin();
  if (environment.apiRouting === 'per-service') {
    const service = resolveApiService(fullPath);
    if (service) {
      origin = resolveServiceOrigin(service);
    }
  }

  const requestPath =
    isUnprefixedProbePath(normalized) && origin
      ? resolveProbeRequestPath(fullPath, origin)
      : fullPath;

  if (!origin) {
    return requestPath;
  }

  return `${origin}${requestPath}`;
}

/**
 * URL de probe de un microservicio (`/health` o `/ready`), sin `/api/v1`.
 * En `per-service` usa el origen de `service` (por defecto `seguridad`).
 *
 * @param probe Nombre del probe Nest.
 * @param service Microservicio destino cuando hay APIM por MS.
 */
export function buildColmenaProbeUrl(
  probe: 'health' | 'ready',
  service: ColmenaApiService = 'seguridad',
): string {
  const probePath = `/${probe}`;
  const origin =
    environment.apiRouting === 'per-service'
      ? resolveServiceOrigin(service)
      : resolveApiOrigin();
  const requestPath = origin ? resolveProbeRequestPath(probePath, origin) : probePath;
  return origin ? `${origin}${requestPath}` : requestPath;
}
